import React, { useCallback, useRef, useState } from 'react';
import {
  ALT_HOST_GETCITIES_URL,
  buildConnectivityVerdict,
  buildDiagReport,
  DIAG_ATTEMPT_TIMEOUT_MS,
  DIAG_MAX_ATTEMPTS,
  downloadTextFile,
  fetchGeoInfo,
  fmtMs,
  getClientEnvInfo,
  maskSensitiveBody,
  nowIso,
  runTimedRequest,
  runTimedRequestWithRetries,
  toText,
} from './diagApi';

const mono = { fontFamily: 'Consolas, Monaco, monospace', fontSize: 13 };

function statusLabel(status) {
  if (status === 'pending') return 'в очереди';
  if (status === 'waiting') return 'отправлен → ждём';
  if (status === 'retrying') return 'повтор…';
  if (status === 'completed') return 'завершён';
  if (status === 'error') return 'ошибка';
  if (status === 'skipped') return 'пропущен';
  return status;
}

function AttemptLines({ step }) {
  if (!step.attempts?.length) return null;
  return (
    <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px dashed #cbd5e1' }}>
      {step.attempts.map((a) => (
        <div
          key={`att-${a.attemptNumber}`}
          style={{
            marginBottom: 4,
            color: a.status === 'completed' ? '#166534' : '#b91c1c',
            fontSize: 11,
          }}
        >
          {a.attemptLabel || `Попытка ${a.attemptNumber}`}:{' '}
          {a.status === 'completed' ? 'ok' : (a.error || 'ошибка')}
          {a.durationMs != null ? `, ${fmtMs(a.durationMs)}` : ''}
          {a.httpStatus != null ? `, HTTP ${a.httpStatus}` : ''}
        </div>
      ))}
      {step.status === 'waiting' && step.currentAttempt && (
        <div style={{ color: '#a16207', fontWeight: 'bold' }}>
          {step.attemptLabel || `Попытка ${step.currentAttempt}`} — ожидаем ответ…
        </div>
      )}
      {step.status === 'retrying' && step.retryNote && (
        <div style={{ color: '#c2410c', fontWeight: 'bold' }}>{step.retryNote}</div>
      )}
      {step.retryNote && step.status === 'completed' && (
        <div style={{ color: '#166534', fontWeight: 'bold', marginTop: 4 }}>{step.retryNote}</div>
      )}
      {step.retryNote && step.status === 'error' && !step.attempts?.some((a) => a.status === 'completed') && (
        <div style={{ color: '#b91c1c', fontWeight: 'bold', marginTop: 4 }}>{step.retryNote}</div>
      )}
    </div>
  );
}

function StepRow({ step }) {
  const bg =
    step.status === 'error' ? '#fee2e2'
      : step.status === 'completed' ? '#ecfdf5'
        : step.status === 'waiting' ? '#fef9c3'
          : '#f3f4f6';

  return (
    <tr style={{ background: bg, verticalAlign: 'top' }}>
      <td style={mono}>{step.label}</td>
      <td style={mono}>
        {step.method} {step.endpoint}
        {step.requestUrl && (
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, wordBreak: 'break-all' }}>
            {step.requestUrl}
          </div>
        )}
      </td>
      <td style={mono}>{statusLabel(step.status)}</td>
      <td style={mono}>{step.startedAt || '—'}</td>
      <td style={mono}>{step.finishedAt || (step.status === 'waiting' ? '…' : '—')}</td>
      <td style={mono}>
        {step.status === 'waiting' || step.status === 'retrying'
          ? (step.attemptLabel || 'ожидание…')
          : fmtMs(step.durationMs)}
        {step.successAttempt ? (
          <div style={{ fontSize: 11, color: '#166534' }}>с {step.successAttempt}-й</div>
        ) : null}
      </td>
      <td style={mono}>{step.httpStatus ?? '—'}</td>
      <td style={mono}>{step.bytes != null ? step.bytes : '—'}</td>
      <td style={{ ...mono, maxWidth: 480, wordBreak: 'break-word' }}>
        {step.requestBody && (
          <div style={{ marginBottom: 6, color: '#1d4ed8' }}>
            → отправлен {step.startedAt ? `(${step.startedAt})` : ''}
            <br />
            тело: {step.requestBody}
          </div>
        )}
        {step.status === 'waiting' && !step.finishedAt && !step.attempts?.length && (
          <div style={{ color: '#a16207' }}>ожидаем ответ сервера…</div>
        )}
        {step.finishedAt && step.status !== 'skipped' && (
          <div style={{ marginBottom: 4, color: '#166534' }}>
            ← ответ {step.finishedAt}
            {step.durationMs != null ? `, ${fmtMs(step.durationMs)}` : ''}
            {step.httpStatus != null ? `, HTTP ${step.httpStatus}` : ''}
          </div>
        )}
        {step.responseHeaders && Object.keys(step.responseHeaders).length > 0 && (
          <div style={{ marginBottom: 4, fontSize: 11, color: '#475569' }}>
            заголовки: {toText(step.responseHeaders)}
          </div>
        )}
        {step.errorHint && (
          <div style={{ marginBottom: 4, color: '#9a3412', fontSize: 11 }}>
            {step.errorHint}
          </div>
        )}
        {step.error ? (
          <span style={{ color: '#b91c1c' }}>
            {step.errorType ? `[${step.errorType}] ` : ''}{step.error}
          </span>
        ) : (
          step.preview || (step.status === 'skipped' ? step.preview : null) || '—'
        )}
        <AttemptLines step={step} />
      </td>
    </tr>
  );
}

export default function DiagPage() {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [running, setRunning] = useState(false);
  const [envInfo, setEnvInfo] = useState(null);
  const [geoInfo, setGeoInfo] = useState(null);
  const [geoStatus, setGeoStatus] = useState(null);
  const [steps, setSteps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [verdict, setVerdict] = useState(null);
  const resultsRef = useRef(null);

  const scrollResultsToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = resultsRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  const updateStep = useCallback((index, row) => {
    setSteps((prev) => {
      const next = [...prev];
      next[index] = row;
      return next;
    });
    scrollResultsToBottom();
  }, [scrollResultsToBottom]);

  const handleDownload = () => {
    const text = buildDiagReport({ phone, envInfo, geoInfo, summary, steps, verdict });
    const stamp = nowIso().replace(/[:.]/g, '-').slice(0, 19);
    downloadTextFile(`diag-${stamp}.txt`, text);
  };

  const runOneStep = async (index, initial, planItem, body, updateStepFn) => {
    const sentAt = nowIso();
    const requestBody = body != null ? toText(maskSensitiveBody(body)) : null;
    const useRetries = (planItem.maxAttempts || 1) > 1;

    const base = {
      ...initial[index],
      label: planItem.label,
      endpoint: planItem.endpoint,
      method: planItem.method || 'POST',
      key: planItem.key,
      requestBody,
      attempts: [],
      maxAttempts: planItem.maxAttempts || 1,
    };

    if (!useRetries) {
      updateStepFn(index, {
        ...base,
        status: 'waiting',
        startedAt: sentAt,
        finishedAt: null,
        durationMs: null,
        httpStatus: null,
        error: null,
        preview: null,
        fullResponse: null,
        bytes: null,
      });

      const row = await runTimedRequest({
        label: planItem.label,
        endpoint: planItem.endpoint,
        method: planItem.method || 'POST',
        body,
        timeoutMs: planItem.timeoutMs || 120000,
        absoluteUrl: planItem.absoluteUrl || null,
      });

      const merged = { ...row, requestBody, startedAt: sentAt, key: planItem.key };
      updateStepFn(index, merged);
      return merged;
    }

    const row = await runTimedRequestWithRetries({
      label: planItem.label,
      endpoint: planItem.endpoint,
      method: planItem.method || 'POST',
      body,
      maxAttempts: planItem.maxAttempts,
      attemptTimeoutMs: planItem.attemptTimeoutMs || DIAG_ATTEMPT_TIMEOUT_MS,
      absoluteUrl: planItem.absoluteUrl || null,
      onProgress: (progress) => {
        updateStepFn(index, {
          ...base,
          ...progress,
          requestBody,
          startedAt: sentAt,
          key: planItem.key,
        });
      },
    });

    const merged = { ...row, requestBody, startedAt: sentAt, key: planItem.key };
    updateStepFn(index, merged);
    return merged;
  };

  const runDiagnostics = async () => {
    if (!phone.trim() || !password) {
      window.alert('Введите логин (телефон) и пароль');
      return;
    }

    setRunning(true);
    setSummary(null);
    setVerdict(null);
    setGeoInfo(null);
    setGeoStatus('определяем локацию по IP…');
    setEnvInfo(getClientEnvInfo());
    scrollResultsToBottom();

    const geo = await fetchGeoInfo();
    setGeoInfo(geo);
    setGeoStatus(null);
    scrollResultsToBottom();

    const plan = [
      {
        label: 'Другой хостинг (gd23.ru)',
        endpoint: '/api/getcities',
        key: 'ping_gd23',
        method: 'POST',
        absoluteUrl: 'https://gd23.ru/api/getcities',
        maxAttempts: DIAG_MAX_ATTEMPTS,
        attemptTimeoutMs: DIAG_ATTEMPT_TIMEOUT_MS,
      },
      {
        label: 'Пинг API (лёгкий)',
        endpoint: '/app-version',
        key: 'ping_app_version',
        method: 'GET',
        maxAttempts: DIAG_MAX_ATTEMPTS,
        attemptTimeoutMs: DIAG_ATTEMPT_TIMEOUT_MS,
      },
      {
        label: 'Пинг API + БД (услуги)',
        endpoint: '/get_services',
        key: 'ping_services',
        method: 'GET',
        maxAttempts: DIAG_MAX_ATTEMPTS,
        attemptTimeoutMs: DIAG_ATTEMPT_TIMEOUT_MS,
      },
      {
        label: 'Вход (клиент)',
        endpoint: '/login',
        key: 'login',
        maxAttempts: DIAG_MAX_ATTEMPTS,
        attemptTimeoutMs: DIAG_ATTEMPT_TIMEOUT_MS,
      },
      { label: 'Главная (/user)', endpoint: '/user', key: 'user', needsUser: true },
      { label: 'Чат — сообщения (fast)', endpoint: '/messages', key: 'messages_fast', needsUser: true, bodyExtra: { messages_fast: '1' } },
      { label: 'Чат — статусы просмотра', endpoint: '/messages/view-meta', key: 'view_meta', needsUser: true, needsMessageIds: true },
      { label: 'Чат — сообщения (полный)', endpoint: '/messages', key: 'messages', needsUser: true },
      { label: 'Чат — непрочитанные', endpoint: '/messages-unread-count', key: 'unread', needsUser: true },
      { label: 'Документы — пакеты', endpoint: '/packs', key: 'packs', needsUser: true },
      { label: 'Документы — непрочитанные', endpoint: '/packs-unread-summary', key: 'packs_unread', needsUser: true },
      { label: 'Задачи — список', endpoint: '/tasks', key: 'tasks', needsUser: true },
    ];

    const initial = plan.map((p) => ({
      label: p.label,
      endpoint: p.endpoint,
      method: p.method || 'POST',
      key: p.key,
      status: 'pending',
      startedAt: null,
      finishedAt: null,
      durationMs: null,
      httpStatus: null,
      error: null,
      preview: null,
      fullResponse: null,
      bytes: null,
      requestBody: null,
    }));
    setSteps(initial);

    let userId = null;
    let messageIds = [];
    const errors = [];
    const stepResults = [];

    for (let i = 0; i < plan.length; i += 1) {
      const p = plan[i];

      if (p.needsUser && !userId) {
        const skipped = {
          ...initial[i],
          status: 'skipped',
          error: 'Нет user_id после логина',
        };
        updateStep(i, skipped);
        stepResults.push(skipped);
        errors.push(p.label);
        continue;
      }

      if (p.needsMessageIds && messageIds.length === 0) {
        const skipped = {
          ...initial[i],
          status: 'skipped',
          startedAt: nowIso(),
          finishedAt: nowIso(),
          durationMs: 0,
          preview: 'Нет message_ids (пустой чат)',
        };
        updateStep(i, skipped);
        stepResults.push(skipped);
        continue;
      }

      let body = null;
      if (p.method !== 'GET' && p.method !== 'HEAD') {
        body = p.key === 'ping_gd23' ? null : { ...(p.bodyExtra || {}) };
      }
      if (body && p.needsUser) body.user_id = userId;
      if (body && p.needsMessageIds) {
        body.user_id = userId;
        body.buh_id = 0;
        body.message_ids = messageIds.slice(0, 50);
      }
      if (body && p.key === 'login') {
        body.phone = phone.trim();
        body.password = password;
      }

      const row = await runOneStep(i, initial, p, body, updateStep);
      stepResults.push(row);

      if (row.status === 'error') {
        errors.push(p.label);
        if (p.key === 'login') break;
      }

      if (p.key === 'login' && row.parsed && !row.parsed.error) {
        userId = row.parsed.id;
      }

      if (p.key === 'messages_fast' && row.parsed) {
        const msgs = row.parsed.messages ?? row.parsed;
        if (Array.isArray(msgs)) {
          messageIds = msgs.map((m) => m.id).filter((id) => id != null);
        }
      }
    }

    const v = buildConnectivityVerdict(stepResults);
    setVerdict(v);
    setSummary({
      userId,
      messageCount: messageIds.length,
      errors,
      loginSuccessAttempt: stepResults.find((s) => s.key === 'login')?.successAttempt ?? null,
      loginAttempts: stepResults.find((s) => s.key === 'login')?.attempts?.length ?? null,
      finishedAt: nowIso(),
    });
    setRunning(false);
    scrollResultsToBottom();
  };

  const hasReport = envInfo || geoInfo || geoStatus || steps.length > 0 || summary || verdict;

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      ...mono,
    }}
    >
      <div style={{
        flexShrink: 0,
        padding: 16,
        borderBottom: '1px solid #ccc',
        background: '#fff',
      }}
      >
        <h1 style={{ fontSize: 18, margin: '0 0 8px' }}>Диагностика API (техническая)</h1>
        <p style={{ margin: '0 0 12px', color: '#444' }}>
          Пинг и логин — до {DIAG_MAX_ATTEMPTS} попыток по {DIAG_ATTEMPT_TIMEOUT_MS / 1000} с (без ожидания 76 с).
          Между попытками: «Вторая попытка», «Третья»…
        </p>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <label>
            Логин (телефон):{' '}
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={running}
              style={{ width: 160 }}
            />
          </label>
          <label>
            Пароль:{' '}
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={running}
              style={{ width: 120 }}
            />
          </label>
          <button type="button" onClick={runDiagnostics} disabled={running}>
            {running ? 'Выполняется…' : 'Старт'}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={!hasReport}
          >
            Скачать .txt
          </button>
        </div>
      </div>

      <div
        ref={resultsRef}
        style={{
          flex: 1,
          overflow: 'auto',
          padding: 16,
          minHeight: 0,
        }}
      >
        {envInfo && (
          <section style={{ marginBottom: 16, border: '1px solid #ccc', padding: 10 }}>
            <strong>Окружение</strong>
            <pre style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(envInfo, null, 2)}
            </pre>
          </section>
        )}

        {(geoStatus || geoInfo) && (
          <section style={{ marginBottom: 16, border: '1px solid #ccc', padding: 10 }}>
            <strong>Локация (по IP)</strong>
            <pre style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>
              {geoStatus || (geoInfo.error && !geoInfo.ip
                ? geoInfo.error
                : [
                  `IP: ${geoInfo.ip || '—'}`,
                  `Город: ${geoInfo.city || '—'}`,
                  `Регион: ${geoInfo.region || '—'}`,
                  `Страна: ${geoInfo.country || '—'}`,
                  `Провайдер: ${geoInfo.isp || '—'}`,
                  `Часовой пояс: ${geoInfo.timezone || '—'}`,
                  `Сервис: ${geoInfo.provider || '—'}`,
                  `Запрос: ${fmtMs(geoInfo.durationMs)}`,
                  geoInfo.attempts?.length
                    ? ['', 'Попытки:', ...geoInfo.attempts.map(
                      (a) => `  ${a.provider}: ${a.ok ? 'ok' : 'fail'} ${a.error || ''} ${fmtMs(a.durationMs)}`
                    )]
                    : [],
                ].flat().join('\n'))}
            </pre>
          </section>
        )}

        {verdict && (
          <section style={{ marginBottom: 16, border: '1px solid #7dd3fc', padding: 10, background: '#f0f9ff' }}>
            <strong>Вывод</strong>
            <pre style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>{verdict}</pre>
          </section>
        )}

        {summary && (
          <section style={{ marginBottom: 16, border: '1px solid #ccc', padding: 10 }}>
            <strong>Итог</strong>
            <pre style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>
              {[
                `user_id: ${summary.userId ?? '—'}`,
                `сообщений в fast-ответе: ${summary.messageCount}`,
                summary.loginAttempts != null
                  ? `попыток login: ${summary.loginAttempts}${summary.loginSuccessAttempt ? `, успех с ${summary.loginSuccessAttempt}-й` : ''}`
                  : null,
                `ошибки: ${summary.errors.length ? summary.errors.join(', ') : 'нет'}`,
                `завершено: ${summary.finishedAt}`,
              ].filter(Boolean).join('\n')}
            </pre>
          </section>
        )}

        {steps.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#e5e7eb', textAlign: 'left' }}>
                  <th>Действие</th>
                  <th>Запрос / URL</th>
                  <th>Статус</th>
                  <th>Отправлен (UTC)</th>
                  <th>Финиш (UTC)</th>
                  <th>мс</th>
                  <th>HTTP</th>
                  <th>байт</th>
                  <th>Отправка / ответ</th>
                </tr>
              </thead>
              <tbody>
                {steps.map((step, idx) => (
                  <StepRow key={`${step.endpoint}-${idx}`} step={step} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
